import { useState, ReactNode, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Checkbox, Tooltip } from "@narmi/design_system";
import { useLocalization } from "@fluent/react";
import { Table } from "semantic-ui-react";
import { DateTime } from "luxon";
import Filters from "byzantine/src/filters";
import DualApprovalRequest, {
  DualApprovalRequestScheduledACHAction,
} from "byzantine/src/DualApprovalRequest";
import Loading from "../Loading";
import { SelectedDualApprovalsMapperType } from "./DualApprovalContainer";
import DualApprovalDrawer from "./DualApprovalDrawer";
import styles from "./DualApproval.module.scss";

type DualApprovalRequestCheckboxType = {
  isSelected: boolean;
  handleToggleCheckbox: () => void;
  isDisabled?: boolean;
};

type DualApprovalRequestHeaderCheckboxType = {
  handleToggleCheckbox: (isSelected: boolean) => void;
};

type TableCellWrapperType = {
  children: ReactNode;
  dualApprovalId: string;
} & React.ComponentProps<typeof Table.Cell>;

type DualApprovalPendingTableType = {
  dualApprovalRequests: DualApprovalRequest[];
  selectedDualApprovals: SelectedDualApprovalsMapperType;
  handleToggleCheckbox: (dualApproval: DualApprovalRequest) => void;
  handleToggleCheckboxHeader: (isSelected: boolean) => void;
  setDualApprovals: React.Dispatch<React.SetStateAction<DualApprovalRequest[]>>;
  handleSingleApprove: (dualApproval: DualApprovalRequest) => void;
  handleSingleReject: (dualApproval: DualApprovalRequest) => void;
  loading: boolean;
};

type DualApprovalTableSortConfigType = {
  column: "date" | "to" | "submittedBy" | "amount" | null;
  direction: "asc" | "desc" | null;
};

export const shouldShowRecipientChangedTooltip = (
  dualApprovalRequest: DualApprovalRequest
) => {
  const { created_at: paymentCreatedAt } = dualApprovalRequest;
  let shouldShowTooltip = false;

  if (
    dualApprovalRequest.action.isACH() ||
    dualApprovalRequest.action.isScheduledACH()
  ) {
    const { lastUpdatedAt: recipientLastUpdatedAt } =
      dualApprovalRequest.action.recipient;

    if (recipientLastUpdatedAt) {
      const now = DateTime.utc();
      const paymentCreatedDate = DateTime.fromJSDate(paymentCreatedAt).toUTC();
      const recipientUpdatedDate = DateTime.fromISO(
        recipientLastUpdatedAt
      ).toUTC();
      const threeDaysBeforePayment = paymentCreatedDate.minus({
        days: 3,
      });
      const threeDaysAfterPayment = paymentCreatedDate.plus({
        days: 3,
      });
      const threeDaysAfterRecipientUpdate = recipientUpdatedDate.plus({
        days: 3,
      });

      // show the "recently changed" tooltip if:
      // - the recipient was updated within 3 days *before* the payment was created,
      //   and today is within 3 days *after* the payment was created OR
      // - today is within 3 days *after* the recipient was updated
      shouldShowTooltip =
        (recipientUpdatedDate >= threeDaysBeforePayment &&
          now <= threeDaysAfterPayment) ||
        now <= threeDaysAfterRecipientUpdate;
    }
  }

  return shouldShowTooltip;
};

export const shouldShowDueSoonTooltip = (
  dualApprovalRequest: DualApprovalRequest
) => {
  let shouldShowTooltip = false;
  if (dualApprovalRequest.action.isScheduledACH()) {
    const { scheduledDate } = dualApprovalRequest.action;
    const today = DateTime.utc().startOf("day");
    const scheduled = DateTime.fromJSDate(scheduledDate).toUTC().startOf("day");
    const oneDayBeforeScheduled = scheduled.minus({ days: 1 });

    // show the "payment must be approved soon" tooltip if:
    // today is day before scheduled date or today is scheduled date
    shouldShowTooltip =
      today.equals(oneDayBeforeScheduled) || today.equals(scheduled);
  }

  return shouldShowTooltip;
};

const DualApprovalCheckbox = ({
  isSelected,
  handleToggleCheckbox,
  isDisabled = false,
}: DualApprovalRequestCheckboxType) => {
  const { l10n } = useLocalization();
  const handleToggle = () => {
    handleToggleCheckbox();
  };

  return (
    <>
      {isDisabled ? (
        <Tooltip
          side="top"
          maxWidth="210px"
          text={l10n.getString("own-request-tooltip")}
        >
          <Checkbox disabled onChange={handleToggle} checked={isSelected} />
        </Tooltip>
      ) : (
        <Checkbox onChange={handleToggle} checked={isSelected} />
      )}
    </>
  );
};

const DualApprovalHeaderCheckbox = ({
  handleToggleCheckbox,
}: DualApprovalRequestHeaderCheckboxType) => {
  const [toggled, setToggled] = useState(false);
  const handleToggle = () => {
    setToggled(!toggled);
    handleToggleCheckbox(!toggled);
  };

  return <Checkbox onChange={handleToggle} checked={toggled} />;
};

const DualApprovalPendingTable = ({
  dualApprovalRequests,
  setDualApprovals,
  selectedDualApprovals,
  handleToggleCheckboxHeader,
  handleToggleCheckbox,
  handleSingleApprove,
  handleSingleReject,
  loading,
}: DualApprovalPendingTableType) => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { dualApprovalId: selectedDualApprovalId } = useParams();
  const [sortConfig, setSortConfig] = useState<DualApprovalTableSortConfigType>(
    { column: "date", direction: "desc" }
  );
  const [clickedDualApprovalIndex, setClickedDualApprovalIndex] =
    useState<number>(-1);
  const showControls = dualApprovalRequests.length > 1;

  useEffect(() => {
    const selectedIndex = dualApprovalRequests.findIndex(
      (approvalRequest) => approvalRequest.uuid === selectedDualApprovalId
    );
    if (selectedIndex > -1) {
      setClickedDualApprovalIndex(selectedIndex);
    } else if (clickedDualApprovalIndex > -1) {
      if (dualApprovalRequests[clickedDualApprovalIndex]?.uuid) {
        navigate(`/${dualApprovalRequests[clickedDualApprovalIndex].uuid}`);
      } else {
        navigate("/");
        setClickedDualApprovalIndex(-1);
      }
    }
  }, [selectedDualApprovalId, dualApprovalRequests]);

  const sortApprovals = (column: DualApprovalTableSortConfigType["column"]) => {
    const isSameColumn = sortConfig.column === column;

    // on first sort (aka on first click), sort should be asc
    const direction =
      isSameColumn && sortConfig.direction === "asc" ? "desc" : "asc";

    const getSortedData = () => {
      let sortedData: DualApprovalRequest[] = [];
      if (column === "date") {
        const achAndWire: DualApprovalRequest[] = [];
        const scheduledACH: (DualApprovalRequest & {
          action: DualApprovalRequestScheduledACHAction;
        })[] = [];

        dualApprovalRequests.forEach((request) => {
          const { action } = request;
          if (action.isACH() || action.isWire()) {
            achAndWire.push(request);
          }
          if (action.isScheduledACH()) {
            scheduledACH.push(
              request as DualApprovalRequest & {
                action: DualApprovalRequestScheduledACHAction;
              }
            );
          }
        });

        scheduledACH.sort((a, b) =>
          direction === "asc"
            ? a.action.scheduledDate.getTime() -
              b.action.scheduledDate.getTime()
            : b.action.scheduledDate.getTime() -
              a.action.scheduledDate.getTime()
        );
        // keep ach and wire at the top, then sort scheduled ACH by scheduled date
        sortedData = [...achAndWire, ...scheduledACH];
      } else if (column === "to") {
        sortedData = dualApprovalRequests.sort((a, b) =>
          direction === "asc"
            ? a.action.recipient?.name.localeCompare(b.action.recipient?.name)
            : b.action.recipient?.name.localeCompare(a.action.recipient?.name)
        );
      } else if (column === "submittedBy") {
        sortedData = dualApprovalRequests.sort((a, b) =>
          direction === "asc"
            ? a.requester.first_name.localeCompare(b.requester.first_name)
            : b.requester.first_name.localeCompare(a.requester.first_name)
        );
      } else if (column === "amount") {
        sortedData = dualApprovalRequests.sort((a, b) =>
          direction === "asc"
            ? a.action.dollarAmount - b.action.dollarAmount
            : b.action.dollarAmount - a.action.dollarAmount
        );
      }
      return sortedData;
    };

    const data = getSortedData();
    setSortConfig({ column, direction });
    setDualApprovals(data);
  };

  const ArrowIcon = ({
    column,
  }: {
    column: DualApprovalTableSortConfigType["column"];
  }) => {
    const { direction, column: columnConfig } = sortConfig;
    const isSameColumn = columnConfig === column;

    const getIconClass = () => {
      if (!isSameColumn || direction === "asc") {
        return "narmi-icon-arrow-up";
      }
      if (direction === "desc") {
        return "narmi-icon-arrow-down";
      }
      return "narmi-icon-arrow-up";
    };

    return (
      <span className={`fontSize--xs margin--left--xxs ${getIconClass()}`} />
    );
  };

  const handleOpenDrawer = (dualApprovalId: string) => {
    navigate(`/${dualApprovalId}`);
  };

  const handleCloseDrawer = () => {
    navigate("/");
    setClickedDualApprovalIndex(-1);
  };

  const handleOnNext = () => {
    const nextIndex = clickedDualApprovalIndex + 1;

    // if it's the last one and there's no next, close the drawer
    if (nextIndex === dualApprovalRequests.length) {
      handleCloseDrawer();
    } else {
      navigate(`/${dualApprovalRequests[nextIndex].uuid}`);
    }
  };

  const handleOnPrev = () => {
    const prevIndex = clickedDualApprovalIndex - 1;

    // if it's the first one and there's no prev, close the drawer
    if (prevIndex < 0) {
      handleCloseDrawer();
    } else {
      navigate(`/${dualApprovalRequests[prevIndex].uuid}`);
    }
  };

  const handleApproveFromDrawer = () => {
    const dualApprovalRequest = dualApprovalRequests[clickedDualApprovalIndex];
    handleSingleApprove(dualApprovalRequest);
  };

  const handleRejectFromDrawer = () => {
    const dualApprovalRequest = dualApprovalRequests[clickedDualApprovalIndex];
    handleSingleReject(dualApprovalRequest);
  };

  const isDrawerOpen = clickedDualApprovalIndex > -1;

  if (loading && !isDrawerOpen) {
    return <Loading />;
  }

  if (dualApprovalRequests.length === 0) {
    return (
      <div className="emptyState">
        {l10n.getString("heading-no-pending-approvals")}
      </div>
    );
  }

  const TableCellWrapper = ({
    children,
    dualApprovalId,
    ...props
  }: TableCellWrapperType) => (
    <Table.Cell {...props} onClick={() => handleOpenDrawer(dualApprovalId)}>
      {children}
    </Table.Cell>
  );

  return (
    <>
      <Table selectable className={styles.table}>
        <Table.Header className={styles.tableHeader}>
          <Table.Row>
            <Table.HeaderCell collapsing>
              <DualApprovalHeaderCheckbox
                handleToggleCheckbox={(isSelected) =>
                  handleToggleCheckboxHeader(isSelected)
                }
              />
            </Table.HeaderCell>
            <Table.HeaderCell onClick={() => sortApprovals("date")} width={3}>
              {l10n.getString("th-scheduled-date")}
              <ArrowIcon column="date" />
            </Table.HeaderCell>
            <Table.HeaderCell onClick={() => sortApprovals("to")} width={3}>
              {l10n.getString("th-to")}
              <ArrowIcon column="to" />
            </Table.HeaderCell>
            <Table.HeaderCell onClick={() => sortApprovals("amount")}>
              {l10n.getString("th-amount")}
              <ArrowIcon column="amount" />
            </Table.HeaderCell>
            <Table.HeaderCell
              onClick={() => sortApprovals("submittedBy")}
              width={3}
            >
              {l10n.getString("th-submitted-by")}
              <ArrowIcon column="submittedBy" />
            </Table.HeaderCell>
            <Table.HeaderCell>{l10n.getString("th-type")}</Table.HeaderCell>
            <Table.HeaderCell>
              {l10n.getString("th-frequency")}
            </Table.HeaderCell>
            <Table.HeaderCell></Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {dualApprovalRequests.map(
            (dualApprovalRequest: DualApprovalRequest) => {
              const { uuid, action, requester } = dualApprovalRequest;
              const { dollarAmount, displayType, recipient, isOwnRequest } =
                action;
              const { name } = recipient;
              const isScheduledACHPayment = action.isScheduledACH();
              const isACHPayment = action.isACH();
              const isWirePayment = action.isWire();
              const displayRecipientChangedTooltip =
                shouldShowRecipientChangedTooltip(dualApprovalRequest);

              let frequency;
              let displayScheduledDate;
              let displayDueSoonTooltip;
              if (isScheduledACHPayment) {
                const { scheduledDate } = action;
                displayDueSoonTooltip =
                  shouldShowDueSoonTooltip(dualApprovalRequest);
                displayScheduledDate = Filters.longMonthDay(
                  scheduledDate.toISOString()
                );
                frequency = action.frequency;
              } else if (isACHPayment || isWirePayment) {
                displayScheduledDate = l10n.getString(
                  "pending-table-one-time-payment"
                );
              }

              return (
                <Table.Row
                  key={`table-row__${uuid}`}
                  className={styles.hoverableRow}
                >
                  <Table.Cell>
                    <DualApprovalCheckbox
                      isDisabled={isOwnRequest}
                      isSelected={Boolean(selectedDualApprovals[uuid])}
                      handleToggleCheckbox={() => {
                        handleToggleCheckbox(dualApprovalRequest);
                      }}
                    />
                  </Table.Cell>
                  <TableCellWrapper dualApprovalId={uuid}>
                    {displayScheduledDate}
                    {displayDueSoonTooltip ? (
                      <Tooltip
                        maxWidth="250px"
                        text={l10n.getString("due-soon-tooltip")}
                      >
                        <span className="fontSize--m narmi-icon-alert-circle fontColor--error margin--left--xs" />
                      </Tooltip>
                    ) : null}
                  </TableCellWrapper>

                  <TableCellWrapper dualApprovalId={uuid}>
                    {name}
                    {displayRecipientChangedTooltip ? (
                      <Tooltip
                        maxWidth="210px"
                        text={l10n.getString(
                          "pending-drawer-recently-changed-copy"
                        )}
                      >
                        <span className="fontSize--m narmi-icon-alert-circle fontColor--error margin--left--xs" />
                      </Tooltip>
                    ) : null}
                  </TableCellWrapper>

                  <TableCellWrapper dualApprovalId={uuid}>
                    {Filters.currency(dollarAmount, { hasDecimal: true })}
                  </TableCellWrapper>

                  <TableCellWrapper dualApprovalId={uuid}>
                    {isOwnRequest ? "You" : requester.getDescription()}
                  </TableCellWrapper>

                  <TableCellWrapper dualApprovalId={uuid}>
                    {displayType}
                  </TableCellWrapper>
                  <TableCellWrapper dualApprovalId={uuid}>
                    {frequency ||
                      l10n.getString("table-cell-frequency-one-time")}
                  </TableCellWrapper>

                  <TableCellWrapper
                    dualApprovalId={uuid}
                    className={styles.viewCtaContainer}
                  >
                    <span className={styles.viewCtaText}>View</span>
                    <span className="fontSize--m narmi-icon-chevron-right" />
                  </TableCellWrapper>
                </Table.Row>
              );
            }
          )}
        </Table.Body>
      </Table>
      <DualApprovalDrawer
        isLoading={loading}
        handleApprove={handleApproveFromDrawer}
        handleReject={handleRejectFromDrawer}
        handleOnNext={handleOnNext}
        handleOnPrev={handleOnPrev}
        isOpen={isDrawerOpen}
        showControls={showControls}
        handleClose={handleCloseDrawer}
        drawerType="pending"
        dualApproval={dualApprovalRequests[clickedDualApprovalIndex]}
      />
    </>
  );
};

export default DualApprovalPendingTable;
